/**
 * Curated soundbite library for the published page.
 *
 * Each entry is a tiny synthesis *recipe* that the runtime plays via
 * Web Audio when its class fires. We deliberately don't ship audio
 * files: zero bundle bloat, zero licensing risk, and identical
 * playback on every browser. The recipes are short (≤350 ms),
 * kid-friendly tones — think "ding", "boop", "cheer", not music.
 *
 * Per-class assignment:
 *   - The composer's "Each class" panel exposes a sound picker (only
 *     enabled when the master `soundOnMatch` widget is on).
 *   - The picker writes `soundId` into `PerClassCustomisation`.
 *   - The runtime reads `perLabel[name].soundId` in `applyMatch` and
 *     plays the matching recipe instead of the generic 660 Hz tone.
 *
 * Adding a new sound: append a new entry and re-run tests. The
 * composer picker, the runtime synth, and the published page all
 * iterate this list — there is no second place to update.
 */

export type SoundOscType = 'sine' | 'triangle' | 'square' | 'sawtooth';

export interface SoundStep {
  /** Hertz. Zero-or-negative skips this step (a rest). */
  freq: number;
  /** Step duration in seconds. */
  dur: number;
  /** Wave shape. Triangle is the default — warm, kid-friendly, no
   *  clipping at small amplitudes. */
  wave?: SoundOscType;
  /** Peak gain (0..0.5). Default 0.18 — loud enough to hear over a
   *  laptop speaker, quiet enough not to startle. */
  gain?: number;
}

export interface SoundDef {
  /** Stable id persisted on `PerClassCustomisation.soundId`. Must
   *  match the runtime's `SOUND_RECIPES` key. */
  id: string;
  /** Picker label. Short, sensory ("Cheer", not "C major triad"). */
  label: string;
  /** Single-emoji preview chip. */
  emoji: string;
  /** One-line description for the picker hover/tap state. */
  hint: string;
  /** Synthesis recipe — one or more sequential tone steps. */
  steps: ReadonlyArray<SoundStep>;
}

/** The curated set. Keep this list short and tasteful (10–14 entries):
 *  too many choices overwhelm the kid and pad the picker UI. */
export const PUBLISH_SOUND_LIBRARY: ReadonlyArray<SoundDef> = [
  {
    id: 'ding',
    label: 'Ding',
    emoji: '🔔',
    hint: 'A clean bell hit — the default success sound.',
    steps: [{ freq: 880, dur: 0.18, wave: 'sine' }],
  },
  {
    id: 'cheer',
    label: 'Cheer',
    emoji: '🎉',
    hint: 'Three rising notes — like nailing a quiz answer.',
    steps: [
      { freq: 523.25, dur: 0.09, wave: 'triangle' },
      { freq: 659.25, dur: 0.09, wave: 'triangle' },
      { freq: 783.99, dur: 0.16, wave: 'triangle' },
    ],
  },
  {
    id: 'pop',
    label: 'Pop',
    emoji: '🫧',
    hint: 'A tiny bubble pop. Friendly and brief.',
    steps: [{ freq: 660, dur: 0.06, wave: 'sine', gain: 0.22 }],
  },
  {
    id: 'boop',
    label: 'Boop',
    emoji: '👉',
    hint: 'Low → low-mid bounce. Cartoonish.',
    steps: [
      { freq: 220, dur: 0.08, wave: 'square', gain: 0.14 },
      { freq: 330, dur: 0.1, wave: 'square', gain: 0.14 },
    ],
  },
  {
    id: 'whoosh',
    label: 'Whoosh',
    emoji: '💨',
    hint: 'A swooping descend — speed and motion.',
    steps: [
      { freq: 1200, dur: 0.05, wave: 'sawtooth', gain: 0.1 },
      { freq: 600, dur: 0.05, wave: 'sawtooth', gain: 0.12 },
      { freq: 220, dur: 0.1, wave: 'sawtooth', gain: 0.1 },
    ],
  },
  {
    id: 'twinkle',
    label: 'Twinkle',
    emoji: '✨',
    hint: 'High sparkle — magical and light.',
    steps: [
      { freq: 1318.5, dur: 0.07, wave: 'sine' },
      { freq: 1568.0, dur: 0.07, wave: 'sine' },
      { freq: 1975.5, dur: 0.1, wave: 'sine' },
    ],
  },
  {
    id: 'thud',
    label: 'Thud',
    emoji: '🥁',
    hint: 'Soft low thump — grounded, like a stamp.',
    steps: [{ freq: 110, dur: 0.14, wave: 'triangle', gain: 0.28 }],
  },
  {
    id: 'beep',
    label: 'Beep',
    emoji: '🤖',
    hint: 'Robotic single beep — playful sci-fi.',
    steps: [{ freq: 1200, dur: 0.1, wave: 'square', gain: 0.12 }],
  },
  {
    id: 'arcade',
    label: 'Arcade',
    emoji: '🕹️',
    hint: 'Power-up bleeps — retro game pickup.',
    steps: [
      { freq: 523.25, dur: 0.05, wave: 'square', gain: 0.14 },
      { freq: 698.46, dur: 0.05, wave: 'square', gain: 0.14 },
      { freq: 880, dur: 0.05, wave: 'square', gain: 0.14 },
      { freq: 1174.66, dur: 0.07, wave: 'square', gain: 0.14 },
    ],
  },
  {
    id: 'meow',
    label: 'Meow',
    emoji: '🐱',
    hint: 'Cartoon cat — sliding mid pitch.',
    steps: [
      { freq: 520, dur: 0.08, wave: 'sawtooth', gain: 0.13 },
      { freq: 720, dur: 0.12, wave: 'sawtooth', gain: 0.13 },
      { freq: 600, dur: 0.08, wave: 'sawtooth', gain: 0.13 },
    ],
  },
  {
    id: 'bark',
    label: 'Bark',
    emoji: '🐶',
    hint: 'Cartoon dog — two short low yelps.',
    steps: [
      { freq: 200, dur: 0.06, wave: 'sawtooth', gain: 0.22 },
      { freq: 0, dur: 0.04 },
      { freq: 220, dur: 0.06, wave: 'sawtooth', gain: 0.22 },
    ],
  },
  {
    id: 'fanfare',
    label: 'Fanfare',
    emoji: '🎺',
    hint: 'Tiny brass call — crowning a big moment.',
    steps: [
      { freq: 392, dur: 0.08, wave: 'triangle' },
      { freq: 392, dur: 0.05, wave: 'triangle' },
      { freq: 523.25, dur: 0.18, wave: 'triangle' },
    ],
  },
];

/** Returns the recipe for a soundId, or undefined if it isn't in the
 *  curated catalogue. Defence-in-depth on a stale persisted id. */
export function getSound(soundId: string | undefined): SoundDef | undefined {
  if (!soundId) return undefined;
  return PUBLISH_SOUND_LIBRARY.find((s) => s.id === soundId);
}

/** Pure side-effect helper: synth a recipe via Web Audio. The caller
 *  passes its own AudioContext so the published page can reuse one
 *  context for all matches (avoids the per-tap ctx leak). */
export function playSoundRecipe(ctx: AudioContext, def: SoundDef): void {
  let t = ctx.currentTime;
  for (const step of def.steps) {
    if (step.freq > 0) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = step.freq;
      osc.type = step.wave ?? 'triangle';
      const peak = step.gain ?? 0.18;
      // Tiny attack + release so we never click at the boundary.
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(peak, t + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + step.dur);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + step.dur + 0.02);
    }
    t += step.dur;
  }
}
